import { useEffect, useState, FC } from 'react'
import { useSelector } from 'react-redux'
import { Conversation } from '@twilio/conversations'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'

import { getParticipantIdsByConversation } from 'features/MerinioChat/helpers'

import { Button as MerinioButton, Card, List } from 'ui'

type Props = {
  conversation: Conversation
}

const ConversationInfo: FC<Props> = ({ conversation }) => {
  const { t } = useTranslation('legacy')
  const usersById = useSelector((state: MStore.RootState) => state.resources.usersById)
  const [activeParticpants, setActiveParticpants] = useState<MData.User[]>([])
  const [loading, setLoading] = useState(false)
  const isGroupConvo = conversation.attributes?.['type'] === 'group'

  useEffect(() => {
    ;(async () => {
      const paricipantsIds = await getParticipantIdsByConversation(conversation)

      const convoUsers = paricipantsIds
        .filter(Boolean)
        .map(id => {
          const user = usersById[id]
          const firstLetter = user.firstName[0].toUpperCase()
          const lastLetter = user.lastName[0].toUpperCase()
          return { ...user, initials: `${firstLetter}${lastLetter}` }
        })
        .sort((a, b) => {
          return a.fullName.localeCompare(b.fullName)
        })

      setActiveParticpants(convoUsers)
    })()
  }, [conversation, usersById, loading])

  const handleRemoveParticipant = async (userId: string) => {
    setLoading(true)

    const participant = await conversation.getParticipantByIdentity(userId)
    await participant?.remove()
    setLoading(false)
  }
  const handleAddParticipant = async (userId: string) => {
    setLoading(true)

    await conversation.add(userId)
    setLoading(false)
  }

  if (!activeParticpants.length) return null

  return (
    <>
      <h4>{t('chat.CONVO.PARTICIPANTS.TITLE')}</h4>
      <ParticipantsAdder loading={loading} activeParticpants={activeParticpants} handleAddParticipant={handleAddParticipant} />
      <ParticipantsList
        isGroupConvo={isGroupConvo}
        loading={loading}
        activeParticpants={activeParticpants}
        handleRemoveParticipant={handleRemoveParticipant}
        createdBy={conversation.createdBy}
      />
    </>
  )
}

type ParticipantsListProps = {
  loading: boolean
  activeParticpants: MData.User[]
  handleRemoveParticipant: (userId: string) => void
  createdBy: string
  isGroupConvo: boolean
}

const ParticipantsList: FC<ParticipantsListProps> = props => {
  const { t } = useTranslation('legacy')

  return (
    <List>
      {props.activeParticpants.map(user => {
        const isAuthor = user._id === props.createdBy
        return (
          <List.Item className="d-flex justify-content-between align-items-center" key={user._id}>
            <span>
              {user.fullName} {isAuthor ? <b>({t('chat.CONVO.OWNER')})</b> : null}
            </span>
            {isAuthor || !props.isGroupConvo ? null : (
              <MerinioButton disabled={props.loading} onClick={() => props.handleRemoveParticipant(user._id)}>
                <i className="fa-solid fa-xmark"></i>
              </MerinioButton>
            )}
          </List.Item>
        )
      })}
    </List>
  )
}

type Option = {
  value: string
  label: string
}
type ParticipantsAdderProps = {
  activeParticpants: MData.User[]
  handleAddParticipant: (userId: string) => Promise<void>
  loading: boolean
}

const ParticipantsAdder: FC<ParticipantsAdderProps> = ({ activeParticpants, handleAddParticipant, loading }) => {
  const { t } = useTranslation('legacy')

  const [selectedUsers, setSelectedUsers] = useState<Option[]>([])
  const users = useSelector((state: MStore.RootState) => state.resources.users)

  const options = users
    .filter(user => !user.archived && !activeParticpants.find(participant => participant._id === user._id))
    .sort((a, b) => a.fullName.localeCompare(b.fullName))
    .map(user => {
      return {
        value: user._id,
        label: user.fullName
      }
    })
    .filter(Boolean)

  return (
    <Card>
      <Card.Body>
        <Select
          value={selectedUsers}
          options={options}
          onChange={selected => {
            const selectedUsers = [...selected]
            setSelectedUsers(selectedUsers)
          }}
          isMulti
          placeholder={t('chat.CONVERSATIONS.SELECT_PARTICIPANTS')}
        />
        <MerinioButton
          onClick={async () => {
            await Promise.all(selectedUsers.map(user => handleAddParticipant(user.value)))
            setSelectedUsers([])
          }}
          type="button"
          className="btn btn-primary mt-4"
          disabled={selectedUsers.length === 0 || loading}
        >
          {t('actions.ADD')}
        </MerinioButton>
      </Card.Body>
    </Card>
  )
}
export { ConversationInfo }
